import {
	DELIVERY_METHOD_KIND,
	VENDOR_AGGREGATION_GROUP,
} from "@dransay/api/models";
import type { DeliveryMethodKind } from "@dransay/db";
import type { ReactNode } from "react";
import type {
	MinimalVendorOption,
	VendorCoords,
} from "../products/filters/sections/vendor-filter.component";
import type { PharmacyMarker } from "./pharmacy-map.types";
import {
	ANY_RADIUS,
	DEFAULT_RADIUS_KM,
	isPharmacyWithinRadiusKm,
	type PharmacyWithDistance,
	type RadiusOption,
	type RadiusOptionValue,
	resolveSessionOrDefaultRadius,
} from "./radius-options";

const MAX_DISPLAYED_MARKERS = 50;

type GetDisplayedMapMarkersParams = {
	vendors: PharmacyMarker[];
	allVendors?: PharmacyMarker[];
	deliveryMethodKind?: DeliveryMethodKind;
	enableTopPharmacyCartPromotion: boolean;
	showAllMapMarkers: boolean;
	previewId: number | null;
	listingCoords: ReadonlyMap<number, VendorCoords>;
};

function hasValidCoordinates(vendor: { lat: number; lng: number }): boolean {
	return Number.isFinite(vendor.lat) && Number.isFinite(vendor.lng);
}

/**
 * Courier accepts express-courier too. The courier list already asks the server
 * for `["courier","express-courier"]` whenever an address is known, so an
 * express-only pharmacy comes back as a courier row — and used to get no marker
 * at all here, since it carries neither a `courier` method nor any other kind
 * the map matches. The picker's own `noResultsKindFromDelivery` already folds
 * the two the same way.
 */
function matchesDeliveryMethod(
	vendor: PharmacyMarker,
	deliveryMethodKind?: DeliveryMethodKind,
): boolean {
	if (!deliveryMethodKind) {
		return true;
	}
	if (deliveryMethodKind === DELIVERY_METHOD_KIND.COURIER) {
		return (
			vendor.deliveryMethods.includes(DELIVERY_METHOD_KIND.COURIER) ||
			vendor.deliveryMethods.includes(DELIVERY_METHOD_KIND.EXPRESS_COURIER)
		);
	}
	return vendor.deliveryMethods.includes(deliveryMethodKind);
}

function sortMarkersByDistanceThenId(
	left: PharmacyMarker,
	right: PharmacyMarker,
): number {
	const leftDistance = left.distance ?? Number.POSITIVE_INFINITY;
	const rightDistance = right.distance ?? Number.POSITIVE_INFINITY;
	if (leftDistance !== rightDistance) {
		return leftDistance - rightDistance;
	}
	return left.id - right.id;
}

function markerFromListingCoords(
	previewId: number,
	coords: VendorCoords,
): PharmacyMarker {
	return {
		id: previewId,
		name: coords.name,
		lat: coords.lat,
		lng: coords.lng,
		distance: coords.distance,
		deliveryMethods: [],
		featured: coords.featured ?? false,
	};
}

export function getDisplayedMapMarkers({
	vendors,
	allVendors,
	deliveryMethodKind,
	enableTopPharmacyCartPromotion,
	showAllMapMarkers,
	previewId,
	listingCoords,
}: GetDisplayedMapMarkersParams): PharmacyMarker[] {
	let base = [...vendors]
		.filter(hasValidCoordinates)
		.filter((vendor) => matchesDeliveryMethod(vendor, deliveryMethodKind))
		.sort(sortMarkersByDistanceThenId)
		.slice(0, MAX_DISPLAYED_MARKERS);
	if (enableTopPharmacyCartPromotion && !showAllMapMarkers) {
		base = base.filter((vendor) => vendor.featured || vendor.id === previewId);
	}
	if (previewId === null) {
		return base;
	}
	if (base.some((marker) => marker.id === previewId)) {
		return base;
	}
	const previewSource = allVendors ?? vendors;
	const inFullList = previewSource.find(
		(vendor) => vendor.id === previewId && hasValidCoordinates(vendor),
	);
	if (inFullList) {
		return [...base, inFullList];
	}
	const fromListing = listingCoords.get(previewId);
	if (fromListing) {
		return [...base, markerFromListingCoords(previewId, fromListing)];
	}
	return base;
}

export function resolveVendorFilterValue(params: {
	useShippingDraft: boolean;
	shippingDraft: string | undefined;
	propsValue: string | undefined;
	shouldUsePharmacyVariant: boolean;
	previewId: number | null;
	draftGroupId: string | null;
	isPickerOpen: boolean;
	effectiveConfirmedValue: string | undefined;
}): string | undefined {
	if (params.useShippingDraft) {
		return params.shippingDraft ?? params.propsValue;
	}
	if (!params.shouldUsePharmacyVariant) {
		return params.propsValue;
	}
	if (params.previewId !== null) {
		return String(params.previewId);
	}
	if (params.isPickerOpen && params.draftGroupId !== null) {
		return params.draftGroupId;
	}
	return params.effectiveConfirmedValue;
}

export function resolveVendorFilterOnChange(params: {
	useShippingDraft: boolean;
	onShippingDraftSelect: (vendorId?: string) => void;
	shouldUsePharmacyVariant: boolean;
	handleChange: (vendorId?: string) => void;
	propsOnChange: (vendorId?: string) => void;
}): (vendorId?: string) => void {
	if (params.useShippingDraft) {
		return params.onShippingDraftSelect;
	}
	if (!params.shouldUsePharmacyVariant) {
		return params.propsOnChange;
	}
	return params.handleChange;
}

export function shouldKeepVendorPickerOpen(
	shouldUsePharmacyVariant: boolean,
	useShippingDraft: boolean,
): boolean {
	return shouldUsePharmacyVariant || useShippingDraft;
}

export function resolveVendorFilterFooterSlot(params: {
	useShippingDraft: boolean;
	shouldUsePharmacyVariant: boolean;
	shippingConfirm: ReactNode;
	mapConfirm: ReactNode;
}): ReactNode {
	if (params.useShippingDraft) {
		return params.shippingConfirm;
	}
	if (params.shouldUsePharmacyVariant) {
		return params.mapConfirm;
	}
	return undefined;
}

export function commitPreviewOrStaleSelection(params: {
	previewId: number | null;
	onConfirmSelection: () => void;
	draftGroupId: string | null;
	hasStaleConfirmedSelection: boolean;
	onChange: (vendorId?: string) => void;
}): void {
	if (params.previewId !== null) {
		params.onConfirmSelection();
		return;
	}
	if (params.draftGroupId !== null) {
		params.onChange(params.draftGroupId);
		return;
	}
	if (params.hasStaleConfirmedSelection) {
		params.onChange(VENDOR_AGGREGATION_GROUP.ALL);
	}
}

export function additionalVendorsFromPreview(
	previewId: number | null,
	vendors: PharmacyMarker[],
): MinimalVendorOption[] {
	if (!previewId) {
		return [];
	}
	const nearbyVendor = vendors.find((vendor) => vendor.id === previewId);
	if (!nearbyVendor) {
		return [];
	}
	return [
		{
			id: String(nearbyVendor.id),
			name: nearbyVendor.name,
			deliveryMethods: nearbyVendor.deliveryMethods.map((kind) => ({
				kind,
				enabled: true,
			})),
			distance: nearbyVendor.distance,
			featured: nearbyVendor.featured ?? false,
		},
	];
}

/**
 * Until the customer picks a radius, follow the session value when it still
 * has results, otherwise the data-driven default (Figma 547). Holds the
 * default radius while the first page is in flight so an empty set does not
 * resolve to "Any radius" and flash `+25km` on the trigger.
 */
export function resolveEffectiveRadius(params: {
	selectedRadius: RadiusOptionValue | null;
	isVendorsLoading: boolean;
	radiusOptions: readonly RadiusOption[];
	sessionRadius: RadiusOptionValue | null;
}): RadiusOptionValue {
	if (params.selectedRadius !== null) {
		return params.selectedRadius;
	}
	if (params.isVendorsLoading) {
		return DEFAULT_RADIUS_KM;
	}
	return resolveSessionOrDefaultRadius(
		params.radiusOptions,
		params.sessionRadius,
	);
}

export function resolveHasStaleConfirmedSelection(params: {
	value: string | undefined;
	confirmedSelection: { vendorId: string; postalCode: string } | null;
	postalCode: string;
}): boolean {
	if (!params.value || !Number.isFinite(Number(params.value))) {
		return false;
	}
	if (params.confirmedSelection?.vendorId !== params.value) {
		return false;
	}
	return params.confirmedSelection.postalCode !== params.postalCode;
}

/**
 * Generic over the row type so the page the map fetched reaches VendorFilter
 * with its real item shape. Erasing it to `{ id }` here would force the picker
 * to re-widen the rows it renders.
 */
export type ExternalVendorsFilteredPayload<TItem> = {
	items: TItem[];
	aggregatedCount?: { top: number; all: number };
	/**
	 * Figma 4662:59239: counts narrowed to the radius-scoped page, for the bulk
	 * rows ("6 of 460 in 10 km" makes the bulk selector a 6). The unscoped
	 * `aggregatedCount` keeps feeding the "x of y" total.
	 */
	scopedAggregatedCount?: { top: number; all: number };
	isLoading?: boolean;
};

export function resolveExternalVendorsFiltered<
	TItem extends { id: string | number; featured?: boolean | null },
>(params: {
	enablePharmacyMap: boolean;
	showRadiusControl: boolean;
	filteredVendors: readonly TItem[];
	visibleMarkers: ReadonlyArray<{ id: number }>;
	aggregatedCount: { top: number; all: number } | null | undefined;
	isLoading: boolean;
}): ExternalVendorsFilteredPayload<TItem> | undefined {
	if (!params.enablePharmacyMap) {
		return undefined;
	}
	if (!params.showRadiusControl) {
		return {
			items: [...params.filteredVendors],
			aggregatedCount: params.aggregatedCount ?? undefined,
			isLoading: params.isLoading,
		};
	}
	const items = params.filteredVendors.filter((item) =>
		params.visibleMarkers.some(
			(marker) => String(marker.id) === String(item.id),
		),
	);
	return {
		items,
		aggregatedCount: params.aggregatedCount ?? undefined,
		// While the page is in flight the scoped set is empty, and zeros here
		// would flash "(0)" group rows — report "unknown" instead.
		scopedAggregatedCount: params.isLoading
			? undefined
			: {
					top: items.filter((item) => item.featured).length,
					all: items.length,
				},
		isLoading: params.isLoading,
	};
}

/**
 * Radius cap handed to the picker list, in km.
 *
 * `undefined` leaves the list uncapped, `null` means "any radius". When the map
 * already supplies the page, its rows are pre-filtered by the selected radius,
 * so capping again inside VendorFilter would apply the same narrowing twice.
 */
export function resolveListRadiusKm(params: {
	hasExternalVendorsFiltered: boolean;
	showRadiusControl: boolean;
	radius: RadiusOptionValue;
}): number | null | undefined {
	if (params.hasExternalVendorsFiltered) {
		return null;
	}
	if (!params.showRadiusControl) {
		return undefined;
	}
	return params.radius === ANY_RADIUS ? null : params.radius;
}

export function optionalWhen<T>(enabled: boolean, value: T): T | undefined {
	if (!enabled) {
		return undefined;
	}
	return value;
}

export function applyPharmacyOrGroupChange(params: {
	vendorId?: string;
	onPharmacy: (vendorId: string) => void;
	onGroup: (vendorId: string) => void;
}): void {
	if (!params.vendorId) {
		return;
	}
	if (Number.isFinite(Number(params.vendorId))) {
		params.onPharmacy(params.vendorId);
		return;
	}
	params.onGroup(params.vendorId);
}

/**
 * The bound the map had before it moved off `/api/nearby-vendors`: that route
 * defaulted to `DEFAULT_MAX_DISTANCE` (10 km) and widened through
 * `RADIUS_EXPANSION_STEPS` (25 km, 50 km) only when the narrower ring came back
 * empty, never past `MAX_DISTANCE_CAP` (50 km).
 *
 * `getVendorsFiltered` sends no `maxDistance` (MKP-2054 removed it so "any
 * radius" could mean any), so with the radius control hidden nothing bounded the
 * markers and the map fell back to the list's unbounded set — pins across
 * Germany. Applying the old steps to the page already loaded restores the old
 * marker set without a second request.
 *
 * Only the markers are bounded. `/api/vendors` never had a distance parameter,
 * so the list was always unbounded and stays that way.
 */
export const LEGACY_MAP_RADIUS_STEPS_KM = [10, 25, 50] as const;

export function applyLegacyMapRadiusBound<T extends PharmacyWithDistance>(
	vendors: readonly T[],
): T[] {
	for (const radiusKm of LEGACY_MAP_RADIUS_STEPS_KM) {
		const within = vendors.filter((vendor) =>
			isPharmacyWithinRadiusKm(vendor, radiusKm),
		);
		if (within.length > 0) {
			return within;
		}
	}
	// Nothing inside 50 km: the old endpoint returned an empty set here too.
	return [];
}
